import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { LayerId, Volume } from '../../../../types';
import { RecorderStore } from '../../../stores/recorder.store';
import { HorizontalDragStartEvent } from '../../../../shared/directives/horizontal-drag-start-event';
import { HorizontalDragEvent } from '../../../../shared/directives/horizontal-drag-event';
import {
  KEYBOARD_ARROW_DOWN_KEY_CODE, KEYBOARD_ARROW_LEFT_KEY_CODE, KEYBOARD_ARROW_RIGHT_KEY_CODE,
  KEYBOARD_ARROW_UP_KEY_CODE, KEYBOARD_ENTER_KEY_CODE
} from '../../../../constants';
import { nearestDividend } from '../../../../shared/helpers/math.helpers';

@Component({
  selector: 'app-timeline-layer-volume',
  templateUrl: './timeline-layer-volume.component.html',
  styleUrls: ['./timeline-layer-volume.component.styl']
})
export class TimelineLayerVolumeComponent implements OnInit {
  @Input() layerId: LayerId;

  @ViewChild('buttonElement') buttonElement: ElementRef;
  @ViewChild('iconElement') iconElement: ElementRef;
  @ViewChild('sliderElement') sliderElement: ElementRef;
  @ViewChild('trackElement') trackElement: ElementRef;

  private volumeBeforeMute: Volume;
  private volumeChangeStep = .1;
  private volume = 1;

  constructor(
    private recorderStore: RecorderStore
  ) {}

  ngOnInit() {
    this.volumeBeforeMute = this.volume;
  }

  get sliderHandlePosition(): number {
    return this.volume * 100;
  }

  get volumeIsHigh(): boolean {
    return this.volume === 1;
  }

  get volumeIsMedium(): boolean {
    return this.volume < 1 && this.volume >= .5;
  }

  get volumeIsLow(): boolean {
    return this.volume > 0 && this.volume < .5;
  }

  get volumeIsMuted(): boolean {
    return this.volume === 0;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();

    if (event.deltaY > 0) {
      this.decreaseVolume();
    } else {
      this.increaseVolume();
    }
  }

  onHorizontalDragStart(event: HorizontalDragStartEvent) {
    this.moveToClientX(event.clientX);
  }

  onHorizontalDrag(event: HorizontalDragEvent) {
    this.moveToClientX(event.clientX);
  }

  private moveToClientX(clientX: number) {
    const track: HTMLElement = this.trackElement.nativeElement;
    const trackLeftPadding = parseInt(getComputedStyle(track).paddingLeft.replace('px', ''), 10);
    const trackClientRect: ClientRect = track.getBoundingClientRect();
    const trackClientLeft: number = trackClientRect.left + trackLeftPadding;
    const trackClientWidth: number = trackClientRect.width;

    if (clientX < trackClientLeft) {
      this.setVolume(0);
    } else if (clientX > trackClientLeft + trackClientWidth) {
      this.setVolume(1);
    } else {
      this.setVolume((clientX - trackClientLeft) / trackClientWidth);
    }
  }

  onIconClick() {
    this.muteOrUnmute();
  }

  private muteOrUnmute() {
    if (this.volume) {
      this.mute();
    } else {
      this.unmute();
    }
  }

  private mute() {
    this.volumeBeforeMute = this.volume;
    this.setVolume(0);
  }

  private unmute() {
    if (this.volumeBeforeMute) {
      this.setVolume(this.volumeBeforeMute);
    } else {
      this.setVolume(1);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    switch (event.keyCode) {
      case KEYBOARD_ARROW_UP_KEY_CODE:
      case KEYBOARD_ARROW_RIGHT_KEY_CODE:
        event.preventDefault();
        this.increaseVolume();
        break;
      case KEYBOARD_ARROW_DOWN_KEY_CODE:
      case KEYBOARD_ARROW_LEFT_KEY_CODE:
        event.preventDefault();
        this.decreaseVolume();
        break;
      case KEYBOARD_ENTER_KEY_CODE:
        event.preventDefault();
        this.muteOrUnmute();
        break;
    }
  }

  private increaseVolume() {
    const volume = this.volume + this.volumeChangeStep;

    if (volume > 1) {
      this.setVolume(1);
    } else {
      this.setVolume(volume);
    }
  }

  private decreaseVolume() {
    const volume = this.volume - this.volumeChangeStep;

    if (volume < 0) {
      this.setVolume(0);
    } else {
      this.setVolume(volume);
    }
  }

  private setVolume(volume) {
    const layerId = this.layerId;

    volume = nearestDividend(volume, .01);

    this.volume = volume;
    this.recorderStore.setLayerVolume({layerId, volume});
  }
}



// WEBPACK FOOTER //
// ./src/app/rec/timeline/timeline-layer/timeline-layer-volume/timeline-layer-volume.component.ts